import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

// Labels are the story language the group asked for; the URLs stay as they were
// so existing links, bookmarks and search results keep working. `key` is what
// the data-testid is built from, so renaming a label can't break a test.
const LINKS = [
  { to: "/", key: "story", label: "Our Story" },
  { to: "/projects", key: "projects", label: "Project Stories" },
  { to: "/team", key: "team", label: "Our Team" },
  { to: "/publications", key: "publications", label: "Selected Publications" },
  { to: "/contact", key: "connect", label: "Connect with Us" },
];

// Only the home page opens on a dark full-bleed image. Every other page opens
// on a light ground, so the bar has to be legible there from the first frame.
const DARK_HEADER_ROUTES = new Set(["/"]);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const toggleRef = useRef(null);

  const overDarkHeader = DARK_HEADER_ROUTES.has(pathname) && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      // Reading progress. The denominator can be 0 on a page shorter than the
      // viewport, which would make this NaN and the rule vanish.
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(y / max, 1) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the overlay on navigation, and lock the page behind it while it is up
  // — an overlay you can scroll the page behind reads as broken.
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const ink = overDarkHeader ? "#F9F8F6" : "#1C2722";
  // Brand gold is 3.56:1 on the bone bar and 4.08:1 over the dark hero — under
  // AA either way at this size. The active item is the one thing in the bar that
  // has to be readable, so it takes the ground-appropriate gold.
  const activeGold = overDarkHeader ? "#B8924A" : "#7E6229";

  return (
    <header
      className={`sticky top-0 z-40 transition-[background-color,backdrop-filter,box-shadow] duration-300 ${
        overDarkHeader
          ? "bg-transparent"
          : "bg-[#F9F8F6]/85 backdrop-blur-xl shadow-[inset_0_-1px_0_0_#D1CEC7]"
      }`}
      data-testid="site-navbar"
      data-over-dark={overDarkHeader ? "true" : "false"}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-[64px] sm:h-[72px] gap-3">
          <Link to="/" className="flex items-center gap-3 lg:gap-4 group min-w-0" data-testid="logo-link">
            {/* 128px rather than the full-size original: this draws at 44–56px,
                and the master is a 1MB PNG that would otherwise be fetched on
                every page load. width/height are set so the bar can't shift as
                it arrives. alt="" because the wordmark beside it says the name —
                announcing it twice helps nobody. */}
            <img
              src="/iwm-logo-128.png"
              srcSet="/iwm-logo-128.png 1x, /iwm-logo-512.png 2x"
              width="128"
              height="128"
              alt=""
              className="h-11 w-11 sm:h-12 sm:w-12 md:h-14 md:w-14 object-contain shrink-0"
            />
            <span
              className="font-serif text-base sm:text-lg md:text-xl tracking-tight transition-colors truncate group-hover:text-[#A17E3B]"
              style={{ color: ink }}
            >
              {settings.org_name || "Innovative Waste Management"}
            </span>
          </Link>

          {/* Desktop nav. The active mark is a dot rather than an underline: at
              five items of very different lengths, underlines of five different
              widths read as noise. */}
          <nav className="hidden lg:flex items-center gap-1 shrink-0">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.key}`}
                className="group relative px-3 py-2"
              >
                {({ isActive }) => (
                  <>
                    <span
                      className="text-[13.5px] xl:text-[14.5px] tracking-wide whitespace-nowrap transition-colors duration-300"
                      style={{ color: isActive ? activeGold : ink }}
                    >
                      {l.label}
                    </span>
                    <span
                      className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0.5 w-1 h-1 bg-[#A17E3B] transition-all duration-300 ${
                        isActive ? "opacity-100 scale-100" : "opacity-0 scale-0 group-hover:opacity-60 group-hover:scale-100"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <button
            ref={toggleRef}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 border shrink-0 transition-colors"
            style={{ color: ink, borderColor: overDarkHeader ? "rgba(249,248,246,0.4)" : "#D1CEC7" }}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Reading progress. Sits on the bottom edge of the bar, 2px, and is
          hidden from assistive tech — it is decoration, not status. */}
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 h-[2px] bg-[#A17E3B] origin-left transition-[width] duration-150 ease-out"
        style={{ width: `${progress * 100}%`, opacity: scrolled ? 1 : 0 }}
      />

      {/* Full-screen overlay rather than a short dropdown: with five long labels
          the dropdown was a cramped list, and at this size the menu can carry
          the same display type as the rest of the site.

          Portalled to <body> rather than rendered here, and this is load
          bearing. The header carries `backdrop-blur`, which makes it the
          containing block for any `fixed` descendant — so an overlay nested
          inside it resolves `inset-0` against the 64px-tall bar and computes to
          zero height. Out at the body it resolves against the viewport, as
          intended. */}
      {open &&
        createPortal(
          <div
            id="mobile-menu"
            className="lg:hidden fixed inset-x-0 bottom-0 top-[64px] sm:top-[72px] z-40 bg-[#1C2722] flex flex-col"
            data-testid="mobile-menu"
          >
            <nav className="flex-1 overflow-y-auto px-6 sm:px-8 py-8">
              {LINKS.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.key}`}
                  className="group flex items-center justify-between gap-4 py-5 border-b border-white/[0.12] fade-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  {({ isActive }) => (
                    <>
                      <span className="flex items-baseline gap-4">
                        <span className="font-mono text-[11px] tracking-widest text-[#9AA5A0]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`font-serif text-[30px] leading-none tracking-tight ${
                            isActive ? "text-[#B8924A]" : "text-[#F9F8F6]"
                          }`}
                        >
                          {l.label}
                        </span>
                      </span>
                      <ArrowUpRight
                        size={20}
                        className="text-[#9AA5A0] shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="px-6 sm:px-8 py-6 border-t border-white/[0.12] text-[11px] font-mono tracking-widest uppercase text-[#9AA5A0]">
              {[settings.city, settings.region].filter(Boolean).join(" · ")}
            </div>
          </div>,
          document.body
        )}

    </header>
  );
};

export default Navbar;
